import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Helper para URLs de imágenes - SOLUCIÓN DEFINITIVA
const getImageUrl = (url) => {
  if (!url) return null;
  // Si ya es URL completa, retornar tal cual
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  // Si es /api/uploads/, agregar backend URL
  if (url.startsWith("/api/uploads/")) return `${BACKEND_URL}${url}`;
  // Si es /uploads/, convertir a /api/uploads/
  if (url.startsWith("/uploads/")) return `${BACKEND_URL}/api${url}`;
  // Cualquier otra URL relativa
  return `${BACKEND_URL}${url}`;
};

// Imagen de fondo por defecto - usar URL completa
const DEFAULT_HERO_BG = `${BACKEND_URL}/api/uploads/hero-choluteca.jpg`;

// Colores de overlay por sección
const OVERLAY_COLORS = {
  noticias: "from-blue-900/90 via-blue-800/85 to-blue-900/90",
  choluteca: "from-emerald-900/90 via-emerald-800/85 to-emerald-900/90",
  honduras: "from-sky-900/90 via-sky-800/85 to-sky-900/90",
  destinos: "from-teal-900/90 via-teal-800/85 to-teal-900/90",
  experiencias: "from-orange-900/90 via-orange-800/85 to-orange-900/90",
  sabores: "from-red-900/90 via-red-800/85 to-red-900/90",
  videos: "from-purple-900/90 via-purple-800/85 to-purple-900/90",
  memorias: "from-amber-900/90 via-amber-800/85 to-amber-900/90",
  galerias: "from-pink-900/90 via-pink-800/85 to-pink-900/90",
  guias: "from-indigo-900/90 via-indigo-800/85 to-indigo-900/90",
  artistas: "from-fuchsia-900/90 via-fuchsia-800/85 to-fuchsia-900/90",
  directorio: "from-cyan-900/90 via-cyan-800/85 to-cyan-900/90",
  contacto: "from-slate-900/90 via-slate-800/85 to-slate-900/90",
  compartir: "from-rose-900/90 via-rose-800/85 to-rose-900/90",
  antesahora: "from-stone-900/90 via-stone-800/85 to-stone-900/90",
  mapa: "from-green-900/90 via-green-800/85 to-green-900/90",
  tienda: "from-emerald-900/90 via-emerald-800/85 to-emerald-900/90",
  deportes: "from-lime-900/90 via-lime-800/85 to-lime-900/90",
  default: "from-primary/90 via-primary/85 to-primary/90",
};

// Cache para configuraciones de secciones
let sectionsCache = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

const getSectionSettings = async () => {
  const now = Date.now();
  if (sectionsCache && (now - cacheTimestamp) < CACHE_DURATION) {
    return sectionsCache;
  }
  
  try {
    const response = await axios.get(`${API}/sections/settings`);
    sectionsCache = response.data.items || [];
    cacheTimestamp = now;
    return sectionsCache;
  } catch (error) {
    console.error("Error fetching section settings:", error);
    return [];
  }
};

export default function PageHero({ 
  section = "default",
  title, 
  subtitle,
  badge,
  badgeIcon: BadgeIcon,
  children,
  className = ""
}) {
  const [heroImageUrl, setHeroImageUrl] = useState(DEFAULT_HERO_BG);
  const overlayColor = OVERLAY_COLORS[section] || OVERLAY_COLORS.default;

  useEffect(() => {
    const loadSectionImage = async () => {
      const settings = await getSectionSettings();
      const sectionSetting = settings.find(s => s.section_id === section);
      if (sectionSetting?.hero_image_url) {
        setHeroImageUrl(sectionSetting.hero_image_url);
      }
    };
    
    if (section !== "default") {
      loadSectionImage();
    }
  }, [section]);

  return (
    <section 
      className={`relative py-16 md:py-24 overflow-hidden ${className}`}
      data-testid={`hero-${section}`}
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${getImageUrl(heroImageUrl)})` }}
      />
      
      {/* Gradient Overlay */}
      <div className={`absolute inset-0 bg-gradient-to-br ${overlayColor}`} />
      
      {/* Decorative elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 right-20 w-64 h-64 bg-white rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-20 w-80 h-80 bg-white rounded-full blur-3xl" />
      </div>

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          {badge && (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white/90 text-sm mb-6">
              {BadgeIcon && <BadgeIcon className="h-4 w-4" />}
              {badge}
            </div>
          )}
          
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
            {title}
          </h1>
          
          {subtitle && (
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              {subtitle}
            </p>
          )}

          {children}
        </motion.div>
      </div>
    </section>
  );
}

export { OVERLAY_COLORS, DEFAULT_HERO_BG };

